import Button from '@shared/components/Button/Button';
import Divider from '@shared/components/Divider/Divider';
import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as BuildingCommunity } from 'assets/icons/BuildingCommunity.svg';
import BoldText from 'components/BoldText';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';

type WorkspaceInviteContentProps = {
  workspaceName: string;
  isLoggedIn?: boolean;
  isLoading?: boolean;
  onJoin: () => void;
  onCancel: () => void;
  joinButtonText?: string;
  cancelButtonText?: string;
};

const WorkspaceInviteContent = ({
  workspaceName,
  isLoggedIn = true,
  isLoading = false,
  onJoin,
  onCancel,
  joinButtonText,
  cancelButtonText,
}: WorkspaceInviteContentProps) => {
  const { t } = useTranslation();
  const { mxDisplayName } = useMxCookies();

  const title = isLoggedIn
    ? t('workspaces.join_workspace_question')
    : t('workspaces.join_workspace_name_question', { workspaceName });

  const getDescription = (): ReactNode => {
    if (!isLoggedIn) return t('workspaces.join_workspace_description_not_logged_in');
    return (
      <Trans
        i18nKey="workspaces.join_workspace_description"
        components={{ bold: <BoldText /> }}
        values={{ workspaceName, userName: mxDisplayName }}
      />
    );
  };

  const primaryButtonText = joinButtonText || (isLoggedIn ? t('join') : t('login'));
  const secondaryButtonText = cancelButtonText || t('cancel');

  return (
    <div className="h-fit w-full flex flex-col m-auto items-center text-center border border-gray-30 bg-gray-10 px-6 pt-10 pb-6 max-w-100 rounded-2xl">
      <GradientIconWithShadow icon={BuildingCommunity} />
      <h4 className="mt-8 mb-2">{title}</h4>
      <p className="text-gray-80 max-w-full break-words">{getDescription()}</p>
      <Divider verticalSpace="lg" />
      <div className="max-w-[312px] w-full flex flex-col items-center gap-4">
        <Button variant="primary" className="w-full" onClick={onJoin} disabled={isLoading} loading={isLoading}>
          {primaryButtonText}
        </Button>
        <Button variant="textSecondary" className="w-fit" onClick={onCancel} disabled={isLoading}>
          {secondaryButtonText}
        </Button>
      </div>
    </div>
  );
};

export default WorkspaceInviteContent;
